import { useCallback } from 'react';
import { getIn, useFormikContext } from 'formik';

import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import MuiSelect from '@mui/material/Select';
import type { SelectChangeEvent } from '@mui/material/Select';

import { ensureDefined, invariant, isDefined } from '@e-socna/utils';
import { ResolverReturnTypeEnum } from '@e-socna/data-layer';
import { EMPTY_STRING } from '@e-socna/empty';

import { checkIfIsId } from '../../utils/check-if-is-id.util';

import type { SelectFieldProps } from './types/props.type';

const YES = 'yes';
const NO = 'no';

export const SelectField = (props: SelectFieldProps) => {
  const { id, label: pLabel, name, required, type, value: pValue } = props;

  const valueValue = pValue?.value;

  invariant(
    !isDefined(valueValue) ||
      (typeof valueValue !== 'number' &&
        (typeof valueValue === 'boolean' || checkIfIsId(valueValue))),
    'SelectField: value must be an id or boolean',
  );

  const value = !isDefined(valueValue)
    ? EMPTY_STRING
    : typeof valueValue === 'boolean'
      ? valueValue
        ? YES
        : NO
      : valueValue;

  const { errors, handleBlur, touched, setFieldTouched, setFieldValue } =
    useFormikContext();

  const { valueMap } = ensureDefined(
    {
      valueMap:
        [
          ResolverReturnTypeEnum.CurrencyId,
          ResolverReturnTypeEnum.UnitId,
          ResolverReturnTypeEnum.VatId,
        ].includes(type) && 'idMap' in props
          ? // eslint-disable-next-line react/destructuring-assignment
            Object.entries(props.idMap).map(([key, val]) => ({
              key,
              label: val.label,
            }))
          : type === ResolverReturnTypeEnum.Boolean
            ? [
                {
                  key: YES,
                  label: 'Oui',
                },
                {
                  key: NO,
                  label: 'Non',
                },
              ]
            : undefined,
    },
    'Map must be defined for Select component',
  );

  const label = !required ? pLabel : `${pLabel} *`;

  const handleChange = useCallback(
    (event: Readonly<SelectChangeEvent>) => {
      const newValue = event.target.value;

      void setFieldValue(name, {
        ...pValue,
        value:
          newValue === EMPTY_STRING
            ? undefined
            : type === ResolverReturnTypeEnum.Boolean
              ? newValue === YES
              : newValue,
      });

      void setFieldTouched(name, true, false);
    },
    [name, setFieldTouched, setFieldValue, type, pValue],
  );

  return (
    <FormControl
      error={!!(getIn(errors, name) && getIn(touched, name))}
      sx={{ flexGrow: 1 }}
    >
      <InputLabel id={`${id}-label`}>{label}</InputLabel>
      <MuiSelect
        fullWidth
        id={`${id}-select`}
        label={label}
        labelId={`${id}-label`}
        name={name}
        onBlur={handleBlur}
        onChange={handleChange}
        required={required}
        value={value}
        variant="outlined"
      >
        <MenuItem key="Undefined" value={EMPTY_STRING}>
          &nbsp;
        </MenuItem>
        {valueMap.map(({ key, label }) => (
          <MenuItem key={key} value={key}>
            {label}
          </MenuItem>
        ))}
      </MuiSelect>
      {!!(getIn(errors, name) && getIn(touched, name)) && (
        <FormHelperText>{getIn(errors, name)}</FormHelperText>
      )}
    </FormControl>
  );
};
